import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Employee } from '../../types';
import Button from '../Form/Button';
import { useCodePrefixes } from '../../hooks/useCodePrefixes';
import { useBranches } from '../../hooks/useBranches';
import { useRanks } from '../../hooks/useRanks';
import { useDocumentTypes } from '../../hooks/useDocumentTypes';
import { employeeDocumentsService, employeesService } from '../../services';
import { uploadFiles } from '../../services/upload.service';
import { nextSequence, maxExistingSequence } from '../../utils/codePrefixUtils';
import { EmployeeBasicInfo } from './EmployeeFormSections';
import { EmployeePersonalInfo } from './EmployeeFormSections';
import { EmployeeEmploymentDetails } from './EmployeeFormSections';
import type { ExistingEmployeeDocument } from './EmployeeFormSections/EmployeePersonalInfo';

export interface PendingEmployeeDocument {
  documentType: string;
  documentNumber?: string;
  issueDate?: string;
  expiryDate?: string;
  filePath: string;
}

interface EmployeeFormProps {
  employee?: Employee;
  onSubmit: (data: Partial<Employee>, pendingDocument?: PendingEmployeeDocument) => void;
  onCancel: () => void;
  userRole?: string;
  /** Optional overrides — e.g. the candidate-hire flow shows "Hire X". */
  title?: string;
  submitLabel?: string;
}

const nationalityOptions = [
  { value: 'Egyptian', label: 'Egyptian' },
  { value: 'Saudi', label: 'Saudi' },
  { value: 'UAE', label: 'UAE' },
  { value: 'Indian', label: 'Indian' },
  { value: 'Pakistani', label: 'Pakistani' },
  { value: 'Filipino', label: 'Filipino' },
  { value: 'Other', label: 'Other' },
];

const documentTypeOptions = [
  { value: 'National ID', label: 'National ID (البطاقة الشخصية)' },
  { value: 'Passport', label: 'Passport (جواز السفر)' },
  { value: 'Seaman Book', label: "Seaman's Book (الجواز البحري)" },
  { value: 'Certificate', label: 'Certificate (شهادة)' },
  { value: 'Medical', label: 'Medical Document (وثيقة طبية)' },
  { value: 'Other', label: 'Other (أخرى)' },
];

// Document types are matched in a case/whitespace-insensitive way and ignore
// parenthetical qualifiers, so "PASSPORT", "Passport" and
// "Passport (جواز السفر)" count as the same type.
const documentTypeKey = (name: string) =>
  name.replace(/\s*\([^)]*\)/g, '').replace(/\s+/g, ' ').trim().toLowerCase();

const EmployeeForm: React.FC<EmployeeFormProps> = ({
  employee,
  onSubmit,
  onCancel,
  userRole = 'admin',
  title,
  submitLabel
}) => {
  // A pre-filled form (candidate hire) passes employee data without a
  // documentId — that is a create, not an edit.
  const isEditing = !!employee?.documentId;
  const { data: branches = [] } = useBranches();
  const [documentFile, setDocumentFile] = useState<File | null>(null);
  const [selectedCodePrefix, setSelectedCodePrefix] = useState<string>('');
  const [employeeNumber, setEmployeeNumber] = useState<string>('001');
  const [documentType, setDocumentType] = useState<string>('');
  const [documentIssueDate, setDocumentIssueDate] = useState<string>('');
  const [documentExpiryDate, setDocumentExpiryDate] = useState<string>('');
  const [existingDocuments, setExistingDocuments] = useState<ExistingEmployeeDocument[]>([]);
  const [uploadError, setUploadError] = useState<string>('');

  const fetchExistingDocuments = React.useCallback(async () => {
    const employeeDocumentId = (employee as any)?.documentId;
    if (!employeeDocumentId) {
      setExistingDocuments([]);
      return;
    }
    const response: any = await employeeDocumentsService.getAll({
      filters: { employee: { documentId: { $eq: employeeDocumentId } } },
    });
    setExistingDocuments(response.data || []);
  }, [employee]);

  useEffect(() => {
    fetchExistingDocuments().catch(() => undefined);
  }, [fetchExistingDocuments]);

  const handleUpdateDocument = async (
    doc: ExistingEmployeeDocument,
    changes: { documentType: string; documentNumber: string; issueDate: string; expiryDate: string; newFile: File | null }
  ) => {
    let filePath = doc.filePath;
    if (changes.newFile) {
      const [uploaded] = await uploadFiles([changes.newFile]);
      filePath = uploaded.url;
    }
    await employeeDocumentsService.update(0, {
      documentId: doc.documentId,
      documentType: changes.documentType,
      documentNumber: changes.documentNumber || null,
      issueDate: changes.issueDate || null,
      expiryDate: changes.expiryDate || null,
      filePath,
    });
    await fetchExistingDocuments();
  };

  const handleDeleteDocument = async (documentId: string) => {
    await employeeDocumentsService.delete(documentId);
    await fetchExistingDocuments();
  };
  
  // currentSequence can lag reality (bulk imports, manual codes), so the
  // "next" number is computed against the employee codes that actually exist.
  const resolveNextSequence = React.useCallback(async (prefix: any): Promise<number> => {
    try {
      const res: any = await employeesService.getAll({
        filters: { code: { $startsWith: prefix.prefix } },
        pagination: { pageSize: 500 },
      });
      const codes = (res?.data || []).map((e: any) => e.code);
      return Math.max(nextSequence(prefix), maxExistingSequence(codes, prefix.prefix) + 1);
    } catch {
      return nextSequence(prefix);
    }
  }, []);

  const queryOptions = useMemo(() => ({
    sort: [{ field: 'createdAt', order: 'desc' as const }],
    pagination: { pageSize: 100 }
  }), []);
  const { data: codePrefixes = [] } = useCodePrefixes(queryOptions);
  const { data: ranks = [] } = useRanks();
  const { data: documentTypes = [] } = useDocumentTypes({ pagination: { pageSize: 100 } });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
    setValue,
    watch
  } = useForm<Employee>({
    defaultValues: employee || {
      travelPermit: false,
      status: 'active',
      payType: 'monthly'
    }
  });

  const payType = watch('payType') || 'monthly';

  // Re-apply the employee values whenever an async option list (ranks,
  // branches, prefixes) finishes loading: the rank/vessel selects are
  // uncontrolled, so a value set before its option exists silently falls
  // back to the first option.
  useEffect(() => {
    if (employee) {
      reset(employee);
      if (employee.code && codePrefixes.length > 0) {
        const matchingPrefix = codePrefixes.find((p: any) => 
          employee.code.startsWith(p.prefix)
        );
        if (matchingPrefix) {
          setSelectedCodePrefix(matchingPrefix.id);
          setEmployeeNumber(employee.code.replace(matchingPrefix.prefix, ''));
        }
      }
    }
  }, [employee, reset, codePrefixes, ranks, branches]);

  // Document types configured in Document Settings appear alongside the
  // built-in options; the stored value is the type's canonical name.
  const mergedDocumentTypeOptions = useMemo(() => {
    const known = new Set(documentTypeOptions.map(o => documentTypeKey(o.value)));
    const extra = (documentTypes as any[])
      .filter(dt => dt.category !== 'car' && dt.name && !known.has(documentTypeKey(dt.name)))
      .map(dt => ({ value: dt.name, label: dt.name }));
    return [...documentTypeOptions, ...extra];
  }, [documentTypes]);

  const onFormSubmit = async (data: Employee) => {
    setUploadError('');
    // All employees are salary-based unless the admin explicitly picks daily.
    if (!data.payType) data.payType = 'monthly' as any;
    let pendingDocument: PendingEmployeeDocument | undefined;
    if (documentFile) {
      if (!documentType) {
        setUploadError('Please select the document type before saving (اختر نوع الوثيقة)');
        return;
      }
      try {
        const [uploaded] = await uploadFiles([documentFile]);
        pendingDocument = {
          documentType,
          documentNumber: data.nationalId || undefined,
          issueDate: documentIssueDate || undefined,
          // For ID cards the document expiry is the card expiry already
          // captured on the employee unless a different date was entered.
          expiryDate: documentExpiryDate || (documentType === 'National ID' ? data.cardExpiryDate : undefined) || undefined,
          filePath: uploaded.url,
        };
      } catch (e: any) {
        setUploadError(e?.message || 'Document upload failed');
        return;
      }
    }
    onSubmit(data, pendingDocument);
  };

  const branchOptions = (branches as any[]).map((branch: any) => ({
    value: branch.documentId || branch.id,
    label: branch.name
  }));

  const rankOptions = useMemo(() => {
    const options = (ranks as any[])
      .map((rank: any) => ({ value: rank.name, label: rank.name }))
      .sort((a, b) => a.label.localeCompare(b.label));
    // Keep the employee's stored rank selectable even if it no longer
    // exists in the ranks list, so editing never silently changes it.
    const currentRank = (employee as any)?.rank;
    if (currentRank && !options.some(o => o.value === currentRank)) {
      options.unshift({ value: currentRank, label: currentRank });
    }
    return options;
  }, [ranks, employee]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200 sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-900">
            {title || (isEditing ? 'Edit Employee' : 'Add New Employee')}
          </h2>
        </div>
        
        <form onSubmit={handleSubmit(onFormSubmit)} className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            <EmployeeBasicInfo
              register={register}
              errors={errors}
              setValue={setValue}
              selectedCodePrefix={selectedCodePrefix}
              setSelectedCodePrefix={setSelectedCodePrefix}
              employeeNumber={employeeNumber}
              setEmployeeNumber={setEmployeeNumber}
              codePrefixes={codePrefixes}
              rankOptions={rankOptions}
              resolveNextSequence={resolveNextSequence}
            />

            <EmployeePersonalInfo
              register={register}
              errors={errors}
              documentFile={documentFile}
              setDocumentFile={setDocumentFile}
              documentType={documentType}
              setDocumentType={setDocumentType}
              documentIssueDate={documentIssueDate}
              setDocumentIssueDate={setDocumentIssueDate}
              documentExpiryDate={documentExpiryDate}
              setDocumentExpiryDate={setDocumentExpiryDate}
              nationalityOptions={nationalityOptions}
              documentTypeOptions={mergedDocumentTypeOptions}
              existingDocuments={existingDocuments}
              onUpdateDocument={handleUpdateDocument}
              onDeleteDocument={handleDeleteDocument}
            />

            <EmployeeEmploymentDetails
              register={register}
              errors={errors}
              userRole={userRole}
              branchOptions={branchOptions}
              payType={payType}
            />
          </div>

          {uploadError && (
            <p className="mt-4 text-sm text-red-600">{uploadError}</p>
          )}

          <div className="flex flex-col sm:flex-row justify-end space-y-2 sm:space-y-0 sm:space-x-3 mt-8 pt-6 border-t border-gray-200">
            <Button
              type="button"
              variant="secondary"
              onClick={onCancel}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full sm:w-auto"
            >
              {isSubmitting ? 'Saving...' : submitLabel || (isEditing ? 'Update Employee' : 'Add Employee')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EmployeeForm;
